/*
  Steigen cart
  1. Instant feedback: prices and the total update the moment a quantity changes or a line is removed,
     while Shopify catches up in the background (the drawer then re-renders with the real numbers).
  2. Picks: one tap adds a suggested product from the cart drawer or cart page.
  3. Quick add stepper: once a sock is in the cart, the bag button on its card becomes minus / count / plus.
  4. Added toast: a small "Added" card with the reward progress line, instead of opening the drawer every time.
  5. Unlock celebration: a full card with confetti when free shipping, free express or the cap unlocks,
     plus a smaller gold moment inside the drawer.
  6. Checkout gates: the free cap must be chosen (or skipped on purpose), and when a small top-up would
     unlock the next reward we ask once.
  7. Product page quantity: the stepper writes straight to the cart (plus adds, minus removes, the number
     shown is the number in the cart). Untouched means not in the cart.
  Wording and amounts come from the theme editor (Theme settings > Steigen picks / Cart rewards).
*/
import { CartUpdateEvent, CartAddEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const cfg = window.stgCart || {};
/* Wording around the amounts comes from Theme settings > Cart rewards > Cart wording (translatable) */
function unlockedText(label) { return (cfg.txtUnlocked || '[reward] unlocked.').replace('[reward]', label); }
const UNLOCK_WORD = ((cfg.txtUnlocked || '[reward] unlocked.').replace('[reward]', '').trim().replace(/\.$/, '')) || 'unlocked';
const UNLOCK_RE = new RegExp(UNLOCK_WORD.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\.\\s*', 'i');
const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const TIERS = (cfg.tiers || []).map(Number).filter(function (n) { return n > 0; });

/* ---------- helpers ---------- */
function sectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
    if (ids.indexOf(el.dataset.sectionId) === -1) ids.push(el.dataset.sectionId);
  });
  return ids;
}
function request(url, body) {
  const options = { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
  if (body) { options.method = 'POST'; options.body = JSON.stringify(body); }
  return fetch(url, options).then(function (r) { return r.json(); });
}
function broadcast(cart, sections, extra) {
  document.dispatchEvent(new CartUpdateEvent(cart, 'stg-cart', Object.assign({ source: 'stg-cart', itemCount: cart.item_count, sections: sections }, extra || {})));
}
let moneySample = null;
function money(cents, sample) {
  const s = (sample || moneySample || cfg.moneySample || '$0.00').trim();
  const pre = (s.match(/^[^\d]*/) || [''])[0];
  const post = (s.match(/[^\d]*$/) || [''])[0].replace(/^[.,]?\d*/, '');
  /* euro style samples (22,95) put the comma before the cents, like Shopify does */
  const comma = /\d,\d{2}(?!\d)/.test(s) && !/\d\.\d{2}(?!\d)/.test(s);
  const n = (cents / 100).toFixed(2);
  const parts = n.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
  return pre + parts.join(comma ? ',' : '.') + post;
}
function setText(el, text) {
  if (!el) return;
  el.textContent = text;
  if (el.hasAttribute('value')) el.setAttribute('value', text);
}
/* What counts towards the rewards: the cart total without the Black Friday free gifts (they are free once Shopify
   applies the gift discounts, but for a moment after they land they can still show their price) */
function rewardTotal(cart) {
  if (!cart || typeof cart.total_price !== 'number') return 0;
  const gifts = (cart.items || []).reduce(function (sum, item) {
    const isGift = (item.properties && item.properties._stg_sale_gift) || item.product_type === 'Gift';
    return isGift ? sum + (item.final_line_price || 0) : sum;
  }, 0);
  return Math.max(0, cart.total_price - gifts);
}
function tierFor(totalCents) {
  let t = 0;
  TIERS.forEach(function (th, i) { if (totalCents >= th) t = i + 1; });
  return t;
}
function nextGoal(totalCents) {
  for (let i = 0; i < TIERS.length; i++) {
    if (totalCents < TIERS[i]) return { gap: TIERS[i] - totalCents, index: i, goal: (cfg.goals || [])[i] || '', label: (cfg.labels || [])[i] || '' };
  }
  return null;
}
function openDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') { drawer.open(); return true; }
  return false;
}
function cartState() {
  let s = null;
  try { s = JSON.parse(sessionStorage.getItem('stg-cart-state') || 'null'); } catch (e) { s = null; }
  return s || {};
}
function saveState(patch) {
  try { sessionStorage.setItem('stg-cart-state', JSON.stringify(Object.assign(cartState(), patch))); } catch (e) { /* private mode */ }
}

/* ---------- 1. instant feedback ---------- */
function rowsOf(root) {
  return Array.prototype.slice.call(root.querySelectorAll('.cart-items__table-row[data-stg-unit]'));
}
function recalc(root) {
  const rows = rowsOf(root);
  let subtotal = 0;
  rows.forEach(function (row) {
    if (row.classList.contains('removing') || row.dataset.stgGone === '1') return;
    const unit = parseInt(row.dataset.stgUnit, 10) || 0;
    const input = row.querySelector('input[type="number"], input[data-cart-quantity]');
    let qty = input ? parseInt(input.value, 10) : parseInt(row.dataset.stgQty, 10);
    if (isNaN(qty)) qty = parseInt(row.dataset.stgQty, 10) || 0;
    const line = unit * qty;
    subtotal += line;
    const priceEl = row.querySelector('.cart-items__price text-component');
    if (priceEl && parseInt(row.dataset.stgQty, 10) !== qty) {
      setText(priceEl, money(line, priceEl.textContent));
      row.classList.add('stg-pending');
    }
  });
  const scope = root.closest('cart-items-component') || root;
  const total = scope.querySelector('[ref="cartTotal"], [data-cart-subtotal]');
  const original = scope.querySelector('.cart-totals__original-value');
  const shown = parseInt(scope.dataset.stgSubtotal || '0', 10);
  if (total && shown && subtotal !== shown) {
    const delta = subtotal - shown;
    const current = parseInt(scope.dataset.stgTotal || '0', 10);
    setText(total, money(Math.max(0, current + delta), total.textContent));
    total.classList.add('stg-pending');
    if (original) setText(original, money(subtotal, original.textContent));
  }
}
function bindInstant() {
  document.addEventListener('click', function (e) {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const root = t.closest('cart-items-component');
    if (!root) return;
    const btn = t.closest('.quantity-plus, .quantity-minus');
    if (btn) {
      setTimeout(function () { recalc(root); }, 0);
      return;
    }
    const remove = t.closest('.cart-items__remove');
    if (remove) {
      const row = remove.closest('.cart-items__table-row');
      if (row) {
        row.dataset.stgGone = '1';
        row.classList.add('stg-removing');
        setTimeout(function () { recalc(root); }, 0);
      }
    }
  }, true);
  document.addEventListener('input', function (e) {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const root = t.closest('cart-items-component');
    if (root && t.closest('.cart-items__table-row')) recalc(root);
  });
}

/* ---------- 2. picks ---------- */
let adding = false;
async function addVariant(variantId, button, quiet) {
  if (adding) return null;
  adding = true;
  if (button) { button.setAttribute('aria-busy', 'true'); button.disabled = true; }
  try {
    const added = await request('/cart/add.js', {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    });
    if (added.status && added.status !== 200) throw new Error(added.description || added.message);
    if (button) { button.classList.add('is-added'); }
    const cart = await request('/cart.js');
    broadcast(cart, added.sections, { stgAdded: Number(variantId), stgQuiet: !!quiet });
    return cart;
  } catch (err) {
    console.warn('[steigen] could not add', err);
    if (button) { button.removeAttribute('aria-busy'); button.disabled = false; }
    return null;
  } finally {
    adding = false;
  }
}
document.addEventListener('click', function (e) {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const btn = t.closest('[data-stg-add]');
  if (!btn) return;
  e.preventDefault();
  addVariant(btn.dataset.stgAdd, btn, true);
});

/* ---------- 3. quick add stepper ---------- */
const cartQty = {};
let cartSnapshot = null;
function readCart(cart) {
  if (!cart || !cart.items) return;
  cartSnapshot = cart;
  Object.keys(cartQty).forEach(function (k) { delete cartQty[k]; });
  cart.items.forEach(function (item) {
    if (item.properties && item.properties._stg_reward) return; /* the free cap is not a shoppable line */
    cartQty[item.variant_id] = (cartQty[item.variant_id] || 0) + item.quantity;
  });
  applySteppers();
  syncForms();
  setTimeout(syncForms, 0); /* after Horizon's own product form bookkeeping has run */
}
function applySteppers() {
  document.querySelectorAll('[data-stg-qa]').forEach(function (step) {
    if (step.classList.contains('is-busy')) return; /* its own write will refresh it */
    const qty = cartQty[step.dataset.variant] || 0;
    const host = step.closest('quick-add-component');
    if (qty > 0) {
      step.hidden = false;
      const count = step.querySelector('[data-stg-qa-count]');
      /* leave the number alone while someone is typing in it */
      if (count && document.activeElement !== count) setCount(count, qty);
      if (host) host.classList.add('stg-in-cart');
    } else {
      step.hidden = true;
      if (host) host.classList.remove('stg-in-cart');
    }
  });
}
/* The shoppable lines for a variant (the free cap line is never one of them) */
function linesFor(id) {
  if (!cartSnapshot || !cartSnapshot.items) return [];
  return cartSnapshot.items.filter(function (i) { return i.variant_id === id && !(i.properties && i.properties._stg_reward); });
}
/* Set how many of a variant sit in the cart. Adds merge into the existing line; when Shopify has split a
   line in two (its free gift discount does that) the change goes on the biggest line; zero clears them all. */
async function writeVariant(id, next) {
  const sections = sectionIds().join(',');
  const lines = linesFor(id);
  const inCart = lines.reduce(function (s, l) { return s + l.quantity; }, 0);
  let result = null;
  if (next > inCart) {
    result = await request('/cart/add.js', { items: [{ id: id, quantity: next - inCart }], sections: sections, sections_url: window.location.pathname });
  } else if (next <= 0) {
    for (let i = 0; i < lines.length; i++) {
      result = await request('/cart/change.js', { id: lines[i].key, quantity: 0, sections: sections, sections_url: window.location.pathname });
    }
  } else if (next < inCart) {
    const target = lines.slice().sort(function (a, b) { return b.quantity - a.quantity; })[0];
    result = await request('/cart/change.js', { id: target.key, quantity: Math.max(0, target.quantity + (next - inCart)), sections: sections, sections_url: window.location.pathname });
  }
  if (result && result.status && result.status !== 200) throw new Error(result.description || result.message);
  return result;
}
/* The count is a box you can type into, like the cart's (older cached markup may still be a plain span) */
function countOf(step) { return step ? step.querySelector('[data-stg-qa-count]') : null; }
function setCount(el, n) {
  if (!el) return;
  if (el.tagName === 'INPUT') { if (el.value !== String(n)) el.value = String(n); }
  else el.textContent = String(n);
}
/* A small note above the stepper, e.g. when they ask for more than we have on the shelf */
function stepNote(step, text) {
  if (!step || !text) return;
  let note = step.querySelector('.stg-qa__note');
  if (!note) {
    note = document.createElement('span');
    note.className = 'stg-qa__note';
    note.setAttribute('role', 'status');
    step.appendChild(note);
  }
  note.textContent = text;
  requestAnimationFrame(function () { note.classList.add('is-live'); });
  clearTimeout(note._t);
  note._t = setTimeout(function () { note.classList.remove('is-live'); }, 2800);
}
function variantTotal(cart, id) {
  return (cart.items || []).reduce(function (s, i) {
    return i.variant_id === id && !(i.properties && i.properties._stg_reward) ? s + i.quantity : s;
  }, 0);
}
/* Taps are never dropped. Every tap (or typed number) moves the count straight away and records what the
   customer is asking for; the cart then catches up, one write at a time, and the drawer, bubble, toast and
   unlock card refresh once when it's done. Three quick taps on plus means three more pairs. */
const QA_MAX = 999;
let wanted = {};
let stepperBusy = false;
function stepsFor(id) { return document.querySelectorAll('[data-stg-qa][data-variant="' + id + '"]'); }
function setWanted(id, next) {
  next = Math.max(0, Math.min(QA_MAX, next));
  wanted[id] = next;
  stepsFor(id).forEach(function (s) {
    s.classList.add('is-busy');
    const c = countOf(s);
    if (c && document.activeElement !== c) setCount(c, next);
  });
  flushSteppers();
}
function stepVariant(step, delta) {
  const id = Number(step.dataset.variant);
  const base = wanted[id] != null ? wanted[id] : (cartQty[id] || 0);
  setWanted(id, base + delta);
}
/* A typed number: tap the count, type 12, press done (or tap away) and the cart holds 12. 0 takes it out. */
function typeVariant(input) {
  const step = input.closest('[data-stg-qa]');
  if (!step) return;
  const id = Number(step.dataset.variant);
  const current = wanted[id] != null ? wanted[id] : (cartQty[id] || 0);
  const raw = String(input.value || '').replace(/\D/g, '');
  if (raw === '') { setCount(input, current); return; }
  const next = Math.min(parseInt(raw, 10), QA_MAX);
  setCount(input, next);
  if (next !== current) setWanted(id, next);
}
async function flushSteppers() {
  if (stepperBusy) return; /* the running pass picks up the newest numbers */
  stepperBusy = true;
  let cart = null;
  let sections = null;
  let added = null;
  let removed = null;
  try {
    for (;;) {
      const id = Object.keys(wanted).map(Number).find(function (k) { return wanted[k] !== (cartQty[k] || 0); });
      if (id == null) break;
      const target = wanted[id];
      const before = cartQty[id] || 0;
      let ok = true;
      try {
        const result = await writeVariant(id, target);
        sections = result && result.sections ? result.sections : null;
      } catch (err) {
        /* asking for more than we have: Shopify fills the cart up to what's on the shelf and reports an error */
        ok = false;
        sections = null;
        console.warn('[steigen] stepper', err);
      }
      cart = await request('/cart.js');
      cartSnapshot = cart;
      const got = variantTotal(cart, id);
      cartQty[id] = got;
      if (got > before) added = id;
      else if (got < before) removed = id;
      if (!ok || got !== target) {
        if (wanted[id] === target) delete wanted[id]; /* stop chasing a number the cart can't hold */
        if (target > got && got > 0) stepNote(stepsFor(id)[0], 'Only ' + got + ' left');
      }
    }
  } catch (err) {
    console.warn('[steigen] stepper', err);
  } finally {
    wanted = {};
    stepperBusy = false;
    document.querySelectorAll('.stg-qa.is-busy').forEach(function (s) { s.classList.remove('is-busy'); });
    if (cart && cart.items) broadcast(cart, sections, { stgAdded: added, stgRemoved: added ? null : removed });
    else request('/cart.js').then(readCart).catch(function () { /* offline: counts settle on the next change */ });
  }
}
function isCountBox(t) { return t instanceof HTMLInputElement && t.hasAttribute('data-stg-qa-count'); }
function bindSteppers() {
  document.addEventListener('click', function (e) {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const step = t.closest('[data-stg-qa]');
    if (!step || isCountBox(t)) return;
    /* any tap on the stepper stays on the stepper: it never falls through to the card and opens the product */
    e.preventDefault();
    e.stopPropagation();
    const plus = t.closest('[data-stg-qa-plus]');
    const minus = t.closest('[data-stg-qa-minus]');
    if (plus || minus) stepVariant(step, plus ? 1 : -1);
  }, true);
  /* Typing in the count: select the number on tap so typing replaces it, digits only, Enter (or done on
     a phone) or tapping away saves it, Escape puts it back */
  /* Browsers drop a selection made on focus when the mouse or finger lifts, so the number is selected
     again on that first click. */
  let freshFocus = null;
  function selectAll(t) { try { t.setSelectionRange(0, t.value.length); } catch (err) { t.select(); } }
  document.addEventListener('focusin', function (e) {
    const t = e.target;
    if (!isCountBox(t)) return;
    freshFocus = t;
    setTimeout(function () { selectAll(t); }, 0);
  });
  document.addEventListener('mouseup', function (e) {
    if (isCountBox(e.target) && freshFocus === e.target) e.preventDefault();
  }, true);
  document.addEventListener('click', function (e) {
    const t = e.target;
    if (!isCountBox(t)) return;
    e.stopPropagation(); /* never let the card treat it as a tap through to the product */
    if (freshFocus === t) { selectAll(t); freshFocus = null; }
  }, true);
  document.addEventListener('focusout', function (e) {
    if (freshFocus === e.target) freshFocus = null;
  });
  document.addEventListener('input', function (e) {
    const t = e.target;
    if (!isCountBox(t)) return;
    const clean = t.value.replace(/\D/g, '').slice(0, 3);
    if (clean !== t.value) t.value = clean;
  });
  document.addEventListener('keydown', function (e) {
    const t = e.target;
    if (!isCountBox(t)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      t.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      const step = t.closest('[data-stg-qa]');
      setCount(t, step ? (cartQty[Number(step.dataset.variant)] || 0) : t.value);
      t.blur();
    }
  });
  document.addEventListener('change', function (e) {
    if (isCountBox(e.target)) typeVariant(e.target);
  });
  /* Every cart change (Horizon's add events share the same event name): refresh counts */
  document.addEventListener(ThemeEvents.cartUpdate, function (event) {
    const cart = event.detail && event.detail.resource;
    if (cart && cart.items) readCart(cart); else request('/cart.js').then(readCart);
  });
  /* product grids re-render on filter and sort: re-apply */
  let timer = null;
  new MutationObserver(function () {
    clearTimeout(timer);
    timer = setTimeout(applySteppers, 120);
  }).observe(document.body, { childList: true, subtree: true });
  request('/cart.js').then(readCart);
}

/* ---------- 4. toast + 5. celebration ---------- */
function removeToast() {
  const old = document.querySelector('.stg-toast');
  if (old) { old.classList.remove('is-live'); setTimeout(function () { old.remove(); }, 250); }
}
function progressLine(total) {
  const n = nextGoal(total);
  if (n) return (cfg.txtProgress || '[amount] more for [goal].').replace('[amount]', money(n.gap)).replace('[goal]', n.goal);
  return cfg.allUnlocked || '';
}
function showToast(cart, variantId) {
  if (cfg.toast === false) return;
  const drawer = document.querySelector('.cart-drawer__dialog');
  if (drawer && drawer.open) return;
  removeToast();
  const item = variantId ? cart.items.find(function (i) { return i.variant_id === Number(variantId); }) : cart.items[0];
  const toast = document.createElement('div');
  toast.className = 'stg-toast';
  toast.setAttribute('role', 'status');
  toast.innerHTML =
    (item && item.image ? '<img class="stg-toast__img" src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x120$1$2') + '" alt="" width="52" height="52">' : '') +
    '<div class="stg-toast__body">' +
    '<p class="stg-toast__title">' + (cfg.toastTitle || 'Added to cart') + (item && item.quantity > 1 ? ' <span class="stg-toast__qty">×' + item.quantity + '</span>' : '') + '</p>' +
    (item ? '<p class="stg-toast__item">' + item.product_title + '</p>' : '') +
    '<p class="stg-toast__progress">' + progressLine(rewardTotal(cart)) + '</p>' +
    '</div>' +
    '<button type="button" class="stg-toast__cta" data-stg-toast-cart>' + (cfg.toastCta || 'View cart') + '<span class="stg-toast__count">' + cart.item_count + '</span></button>' +
    '<button type="button" class="stg-toast__close" aria-label="' + (cfg.txtDismiss || 'Dismiss') + '">×</button>';
  document.body.appendChild(toast);
  requestAnimationFrame(function () { toast.classList.add('is-live'); });
  toast.querySelector('[data-stg-toast-cart]').addEventListener('click', function () { removeToast(); if (!openDrawer()) window.location.href = '/cart'; });
  toast.querySelector('.stg-toast__close').addEventListener('click', removeToast);
  clearTimeout(showToast._t);
  showToast._t = setTimeout(removeToast, 4500);
}
function confetti(layer, count) {
  if (reduce) return;
  const colours = ['#c4a052', '#d4b76a', '#ffffff', '#ff4f70', '#f4f2ed'];
  for (let i = 0; i < count; i++) {
    const p = document.createElement('i');
    const w = 5 + Math.random() * 6;
    p.style.width = w + 'px';
    p.style.height = (w * (0.5 + Math.random())) + 'px';
    p.style.background = colours[i % colours.length];
    p.style.left = (10 + Math.random() * 80) + '%';
    p.style.top = '-6%';
    layer.appendChild(p);
    const drift = (Math.random() - 0.5) * 160;
    p.animate(
      [
        { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
        { transform: 'translate(' + drift + 'px, ' + (260 + Math.random() * 260) + 'px) rotate(' + (360 + Math.random() * 540) + 'deg)', opacity: 0 },
      ],
      { duration: 1600 + Math.random() * 1400, delay: Math.random() * 500, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'forwards' }
    );
  }
}
function closeCelebration() {
  const c = document.querySelector('.stg-celebrate');
  if (!c) return;
  c.classList.remove('is-live');
  setTimeout(function () { c.remove(); }, 300);
}
function showCelebration(tier, cart) {
  if (cfg.celebrate === false) return;
  const label = (cfg.labels || [])[tier - 1] || '';
  if (!label) return;
  removeToast();
  closeCelebration();
  const isLast = tier >= TIERS.length && cfg.hasGiftTier !== false;
  const n = nextGoal(rewardTotal(cart));
  let sub;
  let ctaLabel = cfg.celebrateView || 'View cart';
  if (isLast) {
    sub = cfg.celebrateCap || 'Your free Running Cap is waiting in the cart. Tap black or white and it is yours.';
    ctaLabel = cfg.celebrateChoose || 'Choose my cap';
  } else if (n) {
    sub = (cfg.celebrateNext || 'Keep going: add [gap] more for [goal].').replace('[gap]', money(n.gap)).replace('[goal]', n.goal);
  } else {
    sub = cfg.allUnlocked || '';
  }
  /* Black Friday gifts: say it's already in the cart (the gift adds itself) */
  const note = (cfg.tierNotes || [])[tier - 1];
  if (note && !isLast) sub = note + ' ' + sub;
  /* Run On Us: its own line replaces the usual "keep going" text */
  const subOverride = (cfg.tierSubs || [])[tier - 1];
  if (subOverride) sub = subOverride;
  /* "Keep shopping" is the gold button: every unlock points at the next one. The free cap is the exception,
     because it only lands in the cart once they choose black or white, so "Choose my cap" stays gold. */
  const keepBtn = function (cls) { return '<button type="button" class="stg-celebrate__' + cls + '" data-stg-cel-close>' + (cfg.celebrateKeep || 'Keep shopping') + '</button>'; };
  const cartBtn = function (cls) { return '<button type="button" class="stg-celebrate__' + cls + '" data-stg-cel-cart>' + ctaLabel + '</button>'; };
  const actions = isLast ? cartBtn('primary') + keepBtn('secondary') : keepBtn('primary') + cartBtn('secondary');
  const c = document.createElement('div');
  c.className = 'stg-celebrate';
  c.setAttribute('role', 'dialog');
  c.setAttribute('aria-modal', 'false');
  c.setAttribute('aria-label', unlockedText(label));
  c.innerHTML =
    '<div class="stg-celebrate__confetti" aria-hidden="true"></div>' +
    '<div class="stg-celebrate__card">' +
    '<span class="stg-celebrate__badge" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
    '<p class="stg-celebrate__eyebrow">' + (cfg.celebrateEyebrow || 'Unlocked') + '</p>' +
    '<h2 class="stg-celebrate__title">' + label + '</h2>' +
    '<p class="stg-celebrate__sub">' + sub + '</p>' +
    '<div class="stg-celebrate__actions">' + actions + '</div>' +
    '</div>';
  document.body.appendChild(c);
  confetti(c.querySelector('.stg-celebrate__confetti'), 70);
  requestAnimationFrame(function () { c.classList.add('is-live'); });
  c.querySelector('[data-stg-cel-close]').addEventListener('click', closeCelebration);
  c.addEventListener('click', function (e) { if (e.target === c) closeCelebration(); });
  c.querySelector('[data-stg-cel-cart]').addEventListener('click', function () {
    closeCelebration();
    const opened = openDrawer();
    if (opened && isLast) {
      setTimeout(function () {
        const picker = document.querySelector('.cart-drawer__dialog [data-stg-claim-root]');
        if (picker) { picker.hidden = false; picker.classList.add('is-ready'); picker.scrollIntoView({ block: 'center', behavior: 'smooth' }); picker.querySelectorAll('[data-stg-sizes]:not([hidden])').forEach(function (g) { g.classList.add('is-ready'); }); }
      }, 500);
    }
    if (!opened) window.location.href = '/cart';
  });
  clearTimeout(showCelebration._t);
  showCelebration._t = setTimeout(closeCelebration, 9000);
}
/* Drawer-level gold moment (kept from before) */
const tierState = new WeakMap();
function tierOf(bar) { return bar.querySelectorAll('.stg-rewards__stop.is-on').length; }
function drawerGlow(root, tier) {
  const label = (cfg.labels || [])[tier - 1] || '';
  if (!label) return;
  const summary = root.querySelector('.cart-drawer__summary, .cart-page__summary') || root;
  const totalRow = root.querySelector('.cart-totals__total');
  let toast = summary.querySelector('.stg-unlock');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'stg-unlock';
    toast.setAttribute('role', 'status');
    summary.insertBefore(toast, summary.firstChild);
  }
  const bar = root.querySelector('[data-stg-rewards]');
  const msg = bar && bar.querySelector('.stg-rewards__msg') ? bar.querySelector('.stg-rewards__msg').textContent.replace(/\s+/g, ' ').trim() : '';
  const next = msg && UNLOCK_RE.test(msg) ? msg.split(UNLOCK_RE)[1] || '' : msg;
  toast.innerHTML = '<span class="stg-unlock__spark" aria-hidden="true"></span><span class="stg-unlock__text"><strong>' + unlockedText(label) + '</strong> ' + (next || cfg.celebrateSub || '') + '</span>';
  toast.classList.remove('is-live');
  void toast.offsetWidth;
  toast.classList.add('is-live');
  if (totalRow) { totalRow.classList.remove('stg-glow'); void totalRow.offsetWidth; totalRow.classList.add('stg-glow'); }
  /* confetti inside the drawer (the drawer is a modal layer, so it has to live inside it) */
  const dialog = root.closest('dialog') || root;
  if (!reduce) {
    const layer = document.createElement('div');
    layer.className = 'stg-celebrate__confetti stg-celebrate__confetti--drawer';
    layer.setAttribute('aria-hidden', 'true');
    dialog.appendChild(layer);
    confetti(layer, 40);
    setTimeout(function () { layer.remove(); }, 3600);
  }
  clearTimeout(toast._t);
  toast._t = setTimeout(function () { toast.classList.remove('is-live'); }, 4200);
}
function watchTiers() {
  const check = function () {
    document.querySelectorAll('[data-stg-rewards]').forEach(function (bar) {
      const root = bar.closest('cart-items-component') || document.body;
      const now = tierOf(bar);
      const prev = tierState.get(root);
      if (prev !== undefined && now > prev) drawerGlow(root, now);
      tierState.set(root, now);
    });
  };
  check();
  let raf = 0;
  new MutationObserver(function () {
    if (raf) return;
    raf = window.requestAnimationFrame(function () { raf = 0; check(); });
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
}
/* Page-level: watch every cart change, celebrate new tiers, toast on adds */
function bindMoments() {
  const handle = function (event, isAdd) {
    const detail = event.detail || {};
    const cart = detail.resource;
    if (!cart || typeof cart.total_price !== 'number') return;
    const data = detail.data || {};
    const now = tierFor(rewardTotal(cart));
    const state = cartState();
    const prev = typeof state.tier === 'number' ? state.tier : now;
    saveState({ tier: now });
    const drawer = document.querySelector('.cart-drawer__dialog');
    const drawerOpen = !!(drawer && drawer.open);
    if (now > prev) {
      /* the drawer is a modal layer, so when it is open the gold moment inside it does the celebrating */
      if (!drawerOpen) showCelebration(now, cart);
      return;
    }
    const addedId = data.stgAdded || (isAdd ? detail.sourceId : null);
    if (isAdd || data.stgAdded) showToast(cart, addedId);
  };
  document.addEventListener(ThemeEvents.cartUpdate, function (e) {
    const data = (e.detail && e.detail.data) || {};
    const isAdd = e instanceof CartAddEvent || data.source === 'product-form-component';
    handle(e, isAdd);
  });
  /* first load: remember where the cart sits so we only celebrate real changes */
  request('/cart.js').then(function (cart) {
    if (!cart) return;
    moneySample = null;
    const state = cartState();
    if (typeof state.tier !== 'number') saveState({ tier: tierFor(rewardTotal(cart)) });
  });
}

/* ---------- 6. checkout gates ---------- */
function nudgeKey(tier) { return 'stg-nudged-' + tier; }
function pickForGap(picks) {
  const gap = parseInt(picks.dataset.gap, 10) || 0;
  if (!gap) return null;
  const items = Array.prototype.slice.call(picks.querySelectorAll('[data-stg-pick][data-unlocks="true"] [data-stg-add]'));
  if (!items.length) return null;
  items.sort(function (a, b) { return parseInt(a.closest('[data-stg-pick]').dataset.price, 10) - parseInt(b.closest('[data-stg-pick]').dataset.price, 10); });
  return items[0].closest('[data-stg-pick]');
}
function panelHost(root) {
  return root.querySelector('.cart-drawer__summary, .cart-page__summary') || root;
}
function mountPanel(root, panel) {
  const summary = panelHost(root);
  const old = summary.querySelector('.stg-nudge');
  if (old) old.remove();
  /* Cart page: the summary is a grid whose content spans every row, so a panel added at its top lands below
     everything, out of sight. Put it inside the sticky summary, right above the checkout button instead. */
  const ctas = summary.classList.contains('cart-page__summary') ? summary.querySelector('.cart__ctas') : null;
  if (ctas && ctas.parentElement) ctas.parentElement.insertBefore(panel, ctas);
  else summary.insertBefore(panel, summary.firstChild);
  requestAnimationFrame(function () { panel.classList.add('is-live'); });
}
function proceed(panel, checkoutButton) {
  panel.classList.remove('is-live');
  setTimeout(function () { panel.remove(); }, 250);
  checkoutButton.dataset.stgSkip = '1';
  checkoutButton.click();
}
function showNudge(root, picks, pick, checkoutButton) {
  closeCelebration();
  const gap = parseInt(picks.dataset.gap, 10) || 0;
  const goal = picks.dataset.nextGoal || '';
  const text = (cfg.nudgeText || "You're [gap] from [goal]. Add [item] and it's yours.")
    .replace('[gap]', money(gap))
    .replace('[goal]', goal)
    .replace('[item]', pick.dataset.title + ' (' + money(parseInt(pick.dataset.price, 10)) + ')');
  const panel = document.createElement('div');
  panel.className = 'stg-nudge';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', cfg.nudgeHeading || 'One more thing');
  const img = pick.querySelector('img');
  panel.innerHTML =
    '<div class="stg-nudge__row">' +
    (img ? '<img class="stg-nudge__img" src="' + (img.currentSrc || img.getAttribute('src')) + '" alt="" width="56" height="56">' : '') +
    '<div><p class="stg-nudge__title">' + (cfg.nudgeHeading || 'One more thing') + '</p><p class="stg-nudge__text">' + text + '</p></div>' +
    '</div>' +
    '<div class="stg-nudge__actions">' +
    '<button type="button" class="stg-nudge__add" data-stg-nudge-add>' + (cfg.nudgeAdd || 'Add it and check out') + '</button>' +
    '<button type="button" class="stg-nudge__skip" data-stg-nudge-skip>' + (cfg.nudgeSkip || 'Check out without it') + '</button>' +
    '</div>';
  mountPanel(root, panel);
  panel.querySelector('[data-stg-nudge-skip]').addEventListener('click', function () { proceed(panel, checkoutButton); });
  panel.querySelector('[data-stg-nudge-add]').addEventListener('click', async function () {
    const b = this;
    b.setAttribute('aria-busy', 'true');
    b.textContent = cfg.txtAdding || 'Adding…';
    const cart = await addVariant(pick.dataset.variant, null, true);
    if (cart) {
      b.textContent = cfg.txtCheckoutWait || 'Done. Heading to checkout…';
      setTimeout(function () { window.location.href = '/checkout'; }, 900);
    } else {
      b.removeAttribute('aria-busy');
      b.textContent = cfg.nudgeAdd || 'Add it and check out';
    }
  });
}
let giftGateCheckout = null;
function showGiftGate(root, bar, checkoutButton) {
  const picker = bar.querySelector('[data-stg-claim-root]');
  if (!picker) return false;
  /* one message at a time: the gate replaces the unlock card */
  closeCelebration();
  const panel = document.createElement('div');
  panel.className = 'stg-nudge stg-nudge--gift';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', cfg.giftGateHeading || 'Your free cap');
  panel.innerHTML =
    '<p class="stg-nudge__title">' + (cfg.giftGateHeading || "Don't leave your free cap behind") + '</p>' +
    '<p class="stg-nudge__text">' + (cfg.giftGateText || 'Tap black or white and it is added to your order.') + '</p>' +
    '<div class="stg-nudge__picker"></div>' +
    '<div class="stg-nudge__actions"><button type="button" class="stg-nudge__skip" data-stg-nudge-skip>' + (cfg.giftGateSkip || 'Check out without the cap') + '</button></div>';
  const clone = picker.cloneNode(true);
  clone.hidden = false;
  clone.classList.add('is-ready');
  clone.removeAttribute('data-stg-swap-line');
  clone.querySelectorAll('[data-stg-sizes]:not([hidden])').forEach(function (g) { g.classList.add('is-ready'); });
  panel.querySelector('.stg-nudge__picker').appendChild(clone);
  mountPanel(root, panel);
  panel.querySelector('[data-stg-nudge-skip]').addEventListener('click', function () {
    try { sessionStorage.setItem('stg-gift-skipped', '1'); } catch (e) { /* ignore */ }
    proceed(panel, checkoutButton);
  });
  giftGateCheckout = { button: checkoutButton };
  return true;
}
document.addEventListener('stg:gift-claimed', function () {
  if (!giftGateCheckout) return;
  giftGateCheckout = null;
  setTimeout(function () { window.location.href = '/checkout'; }, 700);
});
/* A number typed into a cart quantity box only reaches Shopify when the box loses focus. Until it lands, the
   cart (and the rewards bar) still show the old number, so the gates below can't see that the free cap has
   just unlocked. Track typed-but-not-landed changes and let them land before checkout. */
const QTY = 'cart-items-component input[name="updates[]"]';
function isQtyBox(t) {
  return t instanceof HTMLInputElement && t.name === 'updates[]' && !!t.closest('cart-items-component');
}
function hasPendingQty() {
  if (document.querySelector('cart-items-component.cart-items-disabled')) return true;
  return Array.prototype.some.call(document.querySelectorAll(QTY), function (input) {
    return input.dataset.stgDirty === '1';
  });
}
function clearPendingQty() {
  document.querySelectorAll(QTY).forEach(function (input) { delete input.dataset.stgDirty; });
}
function nextCartUpdate(timeout) {
  return new Promise(function (resolve) {
    let done = false;
    const finish = function () {
      if (done) return;
      done = true;
      document.removeEventListener(ThemeEvents.cartUpdate, onUpdate);
      resolve();
    };
    /* give the drawer, the bar and the cap picker a moment to re-render */
    const onUpdate = function () { setTimeout(finish, 450); };
    document.addEventListener(ThemeEvents.cartUpdate, onUpdate);
    setTimeout(finish, timeout || 4000);
  });
}
function bindPendingQty() {
  document.addEventListener('input', function (e) {
    if (isQtyBox(e.target)) e.target.dataset.stgDirty = '1';
  }, true);
  /* plus and minus: the change waits a moment before it is sent, so it counts as pending too */
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, function (e) {
    const sel = e.target instanceof Element ? e.target : null;
    if (!sel || !sel.closest('cart-items-component')) return;
    const input = sel.querySelector('input[name="updates[]"]');
    if (input) input.dataset.stgDirty = '1';
  }, true);
  /* Enter in a cart quantity box updates the cart. It never jumps straight to checkout. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || e.isComposing || !isQtyBox(e.target)) return;
    e.preventDefault();
    e.target.blur();
  }, true);
  /* Shopify has the new numbers: nothing is pending any more */
  document.addEventListener(ThemeEvents.cartUpdate, function () {
    if (document.activeElement && isQtyBox(document.activeElement)) return;
    clearPendingQty();
  });
}

function bindGates() {
  document.addEventListener('click', function (e) {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const btn = t.closest('.cart__checkout-button');
    if (!btn) return;
    if (btn.dataset.stgSkip === '1') { delete btn.dataset.stgSkip; return; }
    const root = btn.closest('cart-items-component') || document;

    /* A typed quantity is still on its way: let it land, then check out again so the gates see the real cart
       (if it doesn't land within a few seconds we carry on rather than block checkout) */
    if (btn.dataset.stgWaiting !== '1' && hasPendingQty()) {
      e.preventDefault();
      e.stopPropagation();
      btn.dataset.stgWaiting = '1';
      btn.setAttribute('aria-busy', 'true');
      const active = document.activeElement;
      if (isQtyBox(active)) active.blur();
      nextCartUpdate(4000).then(function () {
        clearPendingQty();
        const fresh = document.getElementById(btn.id) || btn;
        delete btn.dataset.stgWaiting;
        btn.removeAttribute('aria-busy');
        fresh.removeAttribute('aria-busy');
        fresh.click();
      });
      return;
    }

    /* Gate 1: free cap unlocked but not chosen */
    const bar = root.querySelector('[data-stg-rewards]');
    if (cfg.giftGate !== false && bar && bar.dataset.giftUnlocked === 'true' && bar.dataset.giftClaimed !== 'true') {
      let skipped = false;
      try { skipped = sessionStorage.getItem('stg-gift-skipped') === '1'; } catch (err) { skipped = false; }
      if (!skipped && showGiftGate(root, bar, btn)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    /* Gate 2: a small top-up unlocks the next reward */
    if (cfg.nudge === false) return;
    const picks = root.querySelector('[data-stg-picks]');
    if (!picks) return;
    const gap = parseInt(picks.dataset.gap, 10) || 0;
    const tier = picks.dataset.tier;
    const max = (cfg.nudgeMax || 30) * 100;
    if (!gap || gap > max) return;
    let done = false;
    try { done = sessionStorage.getItem(nudgeKey(tier)) === '1'; } catch (err) { done = false; }
    if (done) return;
    const pick = pickForGap(picks);
    if (!pick) return;
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem(nudgeKey(tier), '1'); } catch (err) { /* private mode */ }
    showNudge(root, picks, pick, btn);
  }, true);
}

/* ---------- 7. product page: the quantity stepper is the cart ----------
   Untouched, the stepper shows 1 and nothing is in the cart. The first tap on plus (or a typed number) puts
   that many in the cart. From then on the number shown is the number in the cart: plus and minus change the
   cart straight away, and minus at 1 takes the item out again. "Add to cart" still works from the untouched
   state; once the item is in the cart the button reads "In your cart" and opens the cart instead of adding
   a second helping. Covers the product page and the quick add dialog, never the cart drawer. */
const formBusy = new WeakMap();
function productForms() {
  return Array.prototype.filter.call(document.querySelectorAll('product-form-component'), function (f) {
    return !f.closest('cart-items-component') && !f.classList.contains('quick-add__product-form-component');
  });
}
function formParts(form) {
  const idInput = form.querySelector('input[name="id"]');
  const sel = form.querySelector('quantity-selector-component');
  const input = sel ? sel.querySelector('input[ref="quantityInput"]') : null;
  return {
    id: idInput ? Number(idInput.value) : 0,
    sel: sel,
    input: input,
    minus: sel ? sel.querySelector('.quantity-minus') : null,
    min: input ? (parseInt(input.min, 10) || 1) : 1,
    max: input && input.max ? (parseInt(input.max, 10) || Infinity) : Infinity,
  };
}
function fixButtons(form, p) {
  if (!p.minus || !p.input || p.input.disabled) return;
  const value = parseInt(p.input.value, 10) || 0;
  /* in the cart: minus is always live (at 1 it removes the item); untouched: Horizon's floor applies */
  p.minus.disabled = form.dataset.stgInCart ? false : value <= p.min;
}
function syncForm(form) {
  if (formBusy.get(form)) return; /* a write is in flight: the display already shows where the cart is heading */
  const p = formParts(form);
  if (!p.input) return;
  const qty = p.id ? (cartQty[p.id] || 0) : 0;
  if (qty > 0) {
    if (p.input.value !== String(qty)) p.input.value = qty;
    form.dataset.stgInCart = String(qty);
  } else {
    if (form.dataset.stgInCart) p.input.value = p.min; /* it left the cart: back to the untouched state */
    delete form.dataset.stgInCart;
  }
  form.dataset.stgShown = p.input.value;
  fixButtons(form, p);
}
function syncForms() {
  if (cfg.liveQty === false) return;
  productForms().forEach(syncForm);
}
async function setFormCart(form, next) {
  const p = formParts(form);
  if (!p.id || !p.input || formBusy.get(form)) return;
  const inCart = cartQty[p.id] || 0;
  next = Math.max(0, Math.min(p.max, next));
  if (next === inCart) { syncForm(form); return; }
  formBusy.set(form, true);
  form.classList.add('stg-busy');
  /* show the new number straight away */
  p.input.value = next > 0 ? next : p.min;
  form.dataset.stgShown = p.input.value;
  if (next > 0) form.dataset.stgInCart = String(next); else delete form.dataset.stgInCart;
  fixButtons(form, p);
  try {
    const result = await writeVariant(p.id, next);
    const cart = await request('/cart.js');
    formBusy.delete(form);
    form.classList.remove('stg-busy');
    broadcast(cart, result ? result.sections : null, { stgAdded: next > inCart ? p.id : null, stgRemoved: next < inCart ? p.id : null });
  } catch (err) {
    console.warn('[steigen] quantity', err);
    request('/cart.js').then(readCart); /* put the display back in step with the real cart */
  } finally {
    formBusy.delete(form);
    form.classList.remove('stg-busy');
  }
}
function bindProductForms() {
  if (cfg.liveQty === false) return;
  /* window + capture: runs before Horizon's own handlers, whatever order the scripts loaded in */
  window.addEventListener('click', function (e) {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const form = t.closest('product-form-component');
    if (!form || form.closest('cart-items-component') || form.classList.contains('quick-add__product-form-component')) return;
    const plus = t.closest('.quantity-plus');
    const minus = t.closest('.quantity-minus');
    if (plus || minus) {
      e.preventDefault();
      e.stopPropagation();
      if ((plus || minus).disabled) return;
      const p = formParts(form);
      if (!p.input) return;
      const inCart = p.id ? (cartQty[p.id] || 0) : 0;
      const shown = parseInt(p.input.value, 10) || p.min;
      if (plus) {
        setFormCart(form, (inCart > 0 ? inCart : shown) + 1);
      } else if (inCart > 0) {
        setFormCart(form, inCart - 1);
      } else if (shown > p.min) {
        p.input.value = shown - 1; /* untouched: just count down, nothing in the cart yet */
        form.dataset.stgShown = p.input.value;
        fixButtons(form, p);
      }
      return;
    }
    /* "In your cart": open the cart instead of adding the shown number again */
    if (t.closest('add-to-cart-component') && form.dataset.stgInCart) {
      e.preventDefault();
      e.stopPropagation();
      if (!openDrawer()) window.location.href = '/cart';
    }
  }, true);
  /* Enter in the quantity box would submit the form (and add again) once the item is in the cart */
  window.addEventListener('submit', function (e) {
    const form = e.target instanceof Element ? e.target.closest('product-form-component') : null;
    if (!form || !form.dataset.stgInCart || form.closest('cart-items-component')) return;
    e.preventDefault();
    e.stopPropagation();
    const p = formParts(form);
    const typed = p.input ? parseInt(p.input.value, 10) : NaN;
    if (!isNaN(typed) && String(typed) !== form.dataset.stgShown) setFormCart(form, typed);
    else if (!openDrawer()) window.location.href = '/cart';
  }, true);
  /* A typed quantity (Horizon fires this on blur, even when nothing changed) */
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, function (e) {
    const sel = e.target;
    if (!(sel instanceof Element)) return;
    const form = sel.closest('product-form-component');
    if (!form || form.closest('cart-items-component') || form.classList.contains('quick-add__product-form-component')) return;
    const value = e.detail && typeof e.detail.quantity === 'number' ? e.detail.quantity : NaN;
    setTimeout(function () { fixButtons(form, formParts(form)); }, 0);
    if (isNaN(value) || String(value) === form.dataset.stgShown) return;
    const p = formParts(form);
    const inCart = p.id ? (cartQty[p.id] || 0) : 0;
    if (value <= 0 && inCart === 0) { syncForm(form); return; }
    setFormCart(form, value);
  });
  /* New variant picked (apparel sizes): show that variant's count */
  document.addEventListener(ThemeEvents.variantUpdate, function () { setTimeout(syncForms, 0); });
  /* Horizon re-evaluates the minus button on its own; keep our rule on top */
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('quantity-selector-component').then(function () {
      const C = customElements.get('quantity-selector-component');
      if (!C || !C.prototype || C.prototype.__stg) return;
      const orig = C.prototype.updateButtonStates;
      C.prototype.updateButtonStates = function () {
        if (typeof orig === 'function') orig.apply(this, arguments);
        const form = this.closest('product-form-component');
        if (form && !this.closest('cart-items-component') && form.dataset.stgInCart) fixButtons(form, formParts(form));
      };
      C.prototype.__stg = true;
    });
  }
  /* quick add dialogs arrive later: sync any new form */
  let timer = null;
  new MutationObserver(function () {
    clearTimeout(timer);
    timer = setTimeout(syncForms, 150);
  }).observe(document.body, { childList: true, subtree: true });
}

/* ---------- safety net ----------
   Horizon animates a removed line out while the server re-renders the drawer. When the server answers first,
   the line below can be swept out with the animation (it is still in the cart, just not shown). After every
   cart change, check that the drawer shows exactly the lines in the cart, and re-render it from the server if not. */
function bindSafetyNet() {
  let timer = null;
  let checking = false;
  async function check() {
    if (checking) return;
    const roots = document.querySelectorAll('cart-items-component[data-section-id]');
    if (!roots.length) return;
    checking = true;
    try {
      const cart = await request('/cart.js');
      if (!cart || !cart.items) return;
      const want = cart.items.map(function (i) { return i.key; }).sort().join('|');
      roots.forEach(function (root) {
        if (!root.querySelector('.cart-items__wrapper') && !cart.item_count) return;
        const rows = root.querySelectorAll('.cart-items__table-row[data-key]:not(.removing)');
        const have = Array.prototype.map.call(rows, function (r) { return r.dataset.key; }).sort().join('|');
        if (have !== want) sectionRenderer.renderSection(root.dataset.sectionId, { cache: false });
      });
    } catch (e) {
      /* offline: the next change re-renders anyway */
    } finally {
      checking = false;
    }
  }
  document.addEventListener(ThemeEvents.cartUpdate, function () {
    clearTimeout(timer);
    timer = setTimeout(check, 900);
  });
}

/* ---------- boot ---------- */
const off = /(^|[?&])stgnocart=1/.test(window.location.search);
if (!off) {
  bindInstant();
  bindPendingQty();
  bindGates();
  bindSteppers();
  bindProductForms();
  bindMoments();
  watchTiers();
  bindSafetyNet();
}
